/**
 * SEO helpers — standardize Open Graph / Twitter share metadata across all routes.
 *
 * Facebook, Twitter, LinkedIn, iMessage and Slack all REQUIRE absolute URLs
 * for og:image / twitter:image. Vite-imported assets resolve to relative
 * paths like "/assets/foo-abc.jpg", which scrapers reject.
 *
 * Use `pageMeta(...)` in every route's `head()` so each page gets a
 * consistent, complete OG + Twitter block with absolute image URLs.
 */

import facebookShareFallback from "@/assets/original/hero-shade.jpg";

export const SITE_URL = "https://texasmadeshade.com";
export const SITE_NAME = "Texas Made Shade & Covers";
export const TWITTER_HANDLE = "@texasmadeshade";
export const DEFAULT_OG_LOCALE = "en_US";
export const DEFAULT_OG_IMAGE_WIDTH = "1200";
export const DEFAULT_OG_IMAGE_HEIGHT = "630";

type ImageInput = string | { src?: string } | undefined | null;

/** Convert any imported asset or path to an absolute URL on the canonical domain. */
export function absUrl(input: ImageInput): string {
  if (!input) return SITE_URL;
  const raw = typeof input === "string" ? input : (input.src ?? "");
  if (!raw) return SITE_URL;
  if (/^https?:\/\//i.test(raw)) return raw;
  return `${SITE_URL}${raw.startsWith("/") ? "" : "/"}${raw}`;
}

/** Build an absolute canonical URL for a route path (e.g. "/awnings"). */
export function canonicalUrl(path: string = "/"): string {
  if (/^https?:\/\//i.test(path)) return path;
  if (!path || path === "/") return `${SITE_URL}/`;
  return `${SITE_URL}${path.startsWith("/") ? "" : "/"}${path}`;
}

export interface PageMetaInput {
  /** <title> tag (and the default for og:title / twitter:title). */
  title: string;
  /** Meta description (and the default for og:description / twitter:description). */
  description: string;
  /** Hero/cover image — anything `absUrl()` accepts. Becomes og:image + twitter:image. */
  image?: ImageInput;
  /** Path on this site, e.g. "/awnings". Used for og:url + canonical. */
  path?: string;
  /** og:type. Defaults to "website". Use "article" for blog posts, "product" for shop items. */
  type?: "website" | "article" | "product";
  /**
   * Optional shorter title used for BOTH og:title and twitter:title.
   * Use when you want a punchier social-share headline than the full SEO `<title>`.
   */
  socialTitle?: string;
  /** Optional shorter description used for BOTH og:description and twitter:description. */
  socialDescription?: string;
  /** Image alt text. Defaults to `title`. */
  imageAlt?: string;
  /** Optional comma-separated keywords. */
  keywords?: string;
  /** Article-only fields (used when type === "article"). */
  article?: {
    publishedTime?: string;
    modifiedTime?: string;
    author?: string;
    section?: string;
    tags?: string[];
  };
}

type Meta = Record<string, string>;

/** Preserve each page’s authored service and location focus. */
const MAX_DESCRIPTION_LENGTH = 160;

/**
 * Hard-clamp a description to the 160-char cap at a word boundary. Base
 * descriptions authored per-route sometimes run long; Google truncates them
 * mid-sentence, so we trim cleanly and end with an ellipsis instead.
 */
function clampDescription(text: string): string {
  const t = text.replace(/\s+/g, " ").trim();
  if (t.length <= MAX_DESCRIPTION_LENGTH) return t;
  const slice = t.slice(0, MAX_DESCRIPTION_LENGTH - 1);
  const cut = slice.lastIndexOf(" ");
  return `${(cut > 80 ? slice.slice(0, cut) : slice).replace(/[\s,;:.\-—]+$/, "")}…`;
}

/**
 * Geo / local-SEO meta block emitted on EVERY page. Search engines and local
 * directories read these to associate the page with our service area, with
 * San Antonio and Austin named first because those are our target markets.
 */
export const PRIMARY_CITIES = "San Antonio, Austin";

export const MAJOR_TEXAS_CITIES = [
  "San Antonio",
  "Austin",
  "Seguin",
  "New Braunfels",
  "San Marcos",
  "Boerne",
  "Kerrville",
  "Fredericksburg",
  "Houston",
  "Dallas",
  "Fort Worth",
  "Arlington",
  "Plano",
  "Frisco",
  "El Paso",
  "Corpus Christi",
  "Laredo",
  "Lubbock",
  "Amarillo",
  "Waco",
  "College Station",
  "Round Rock",
  "Cedar Park",
  "Georgetown",
  "Pflugerville",
  "Kyle",
  "Buda",
  "Victoria",
  "Del Rio",
  "Eagle Pass",
  "Uvalde",
  "Brownsville",
  "McAllen",
  "Harlingen",
  "Galveston",
  "The Woodlands",
  "Sugar Land",
  "Katy",
  "Conroe",
  "Midland",
  "Odessa",
  "Abilene",
  "San Angelo",
  "Tyler",
  "Killeen",
  "Temple",
  "Bryan",
  "Beaumont",
  "Wichita Falls",
  "Texarkana",
  "Garland",
  "Irving",
  "Grand Prairie",
  "Mesquite",
  "McKinney",
  "Allen",
  "Denton",
  "Carrollton",
  "Richardson",
  "Lewisville",
  "Flower Mound",
  "Mansfield",
  "Pearland",
  "League City",
  "Pasadena",
  "Baytown",
  "Missouri City",
  "Spring",
  "Cypress",
  "Rosenberg",
  "New Caney",
  "Leander",
  "Lockhart",
  "Bastrop",
  "Boerne",
  "Bandera",
  "Castroville",
  "Floresville",
  "Pleasanton",
  "Devine",
  "Hondo",
  "Gonzales",
  "Luling",
  "La Vernia",
  "Schertz",
  "Cibolo",
  "Universal City",
  "Converse",
  "Helotes",
  "Bulverde",
  "Canyon Lake",
  "Marble Falls",
  "Burnet",
  "Llano",
  "Junction",
  "Edinburg",
  "Mission",
  "Pharr",
  "Longview",
  "Lufkin",
  "Nacogdoches",
  "Port Arthur",
  "Rockport",
  "Port Aransas",
  "Kingsville",
  "Alice",
  "Beeville",
  "Cuero",
  "Yoakum",
  "Hallettsville",
  "Columbus",
  "Brenham",
  "Giddings",
  "La Grange",
] as const;


/**
 * Sitewide keyword block. Page-specific keywords come first (they carry the
 * most weight), then core service terms, then city-scoped awning phrases.
 * We only city-scope the first 30 cities so the keywords tag stays a usable
 * length — the full statewide list still ships in the geo/location tags.
 */
export function keywordsWithLocal(extra?: string): string {
  const SITEWIDE_KEYWORDS = [
    "retractable awnings",
    "retractable awnings San Antonio",
    "retractable awnings Austin",
    "motorized screens Texas",
    "louvered pergolas Texas",
    "shade structures San Antonio",
    "patio covers Austin",
    "custom canvas Texas",
    "commercial awnings Texas",
    "awning recover Texas",
    ...MAJOR_TEXAS_CITIES.slice(0, 30).map((c) => `awnings ${c} TX`),
  ].join(", ");
  return [extra?.trim(), SITEWIDE_KEYWORDS]
    .filter((s): s is string => !!s && s.length > 0)
    .join(", ");
}


export function geoMeta(extraKeywords?: string): Meta[] {
  return [
    { name: "keywords", content: keywordsWithLocal(extraKeywords) },
    { name: "geo.region", content: "US-TX" },
    // Primary markets first (San Antonio, Austin), then every major Texas city
    // so the site reads statewide instead of single-city.
    { name: "geo.placename", content: `${PRIMARY_CITIES}, Seguin, ${MAJOR_TEXAS_CITIES.join(", ")}, Texas` },
    { name: "geo.position", content: "29.568756;-97.964743" },
    { name: "ICBM", content: "29.568756, -97.964743" },
    { name: "location", content: `${PRIMARY_CITIES}, ${MAJOR_TEXAS_CITIES.join(", ")}, Texas, USA` },
    { name: "coverage", content: `Texas — ${MAJOR_TEXAS_CITIES.join(", ")}` },
    { name: "distribution", content: "global" },
    { name: "target_city", content: `${PRIMARY_CITIES}, ${MAJOR_TEXAS_CITIES.join(", ")}` },
    { name: "cities", content: MAJOR_TEXAS_CITIES.join(", ") },
    { name: "service-area", content: MAJOR_TEXAS_CITIES.join(", ") },
    { name: "business:contact_data:region", content: "TX" },
    { name: "business:contact_data:country_name", content: "USA" },
    { property: "place:location:latitude", content: "29.568756" },
    { property: "place:location:longitude", content: "-97.964743" },
  ];
}

/** Build a complete OG + Twitter meta block for a route's head(). */
export function pageMeta(input: PageMetaInput): Meta[] {
  const {
    title,
    description: rawDescription,
    image,
    path = "/",
    type = "website",
    socialTitle,
    socialDescription,
    imageAlt = title,
    keywords,
    article,
  } = input;

  const description = clampDescription(rawDescription);

  const url = canonicalUrl(path);
  const img = image ? absUrl(image) : undefined;
  const ogTitle = socialTitle ?? title;
  const ogDesc = socialDescription ?? description;

  const meta: Meta[] = [
    { title },
    { name: "description", content: description },

    // Crawler / indexing directives — explicitly invite Google, Bing, and other
    // bots to crawl + index every page, with rich previews enabled.
    { name: "robots", content: "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" },
    { name: "googlebot", content: "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" },
    { name: "bingbot", content: "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" },
    { name: "slurp", content: "index, follow" },
    { name: "duckduckbot", content: "index, follow" },
    { name: "yandex", content: "index, follow" },
    // Re-crawl scheduling hints — tells crawlers this page is expected to be
    // refreshed and revisited, and tags the page with its language market.
    { name: "revisit-after", content: "7 days" },
    { name: "rating", content: "general" },
    { name: "language", content: "English" },
    { name: "google", content: "notranslate" },
    { name: "referrer", content: "strict-origin-when-cross-origin" },

    // Open Graph
    { property: "og:type", content: type },
    { property: "og:site_name", content: SITE_NAME },
    { property: "og:locale", content: DEFAULT_OG_LOCALE },
    { property: "og:url", content: url },
    { property: "og:title", content: ogTitle },
    { property: "og:description", content: ogDesc },

    // Twitter
    { name: "twitter:card", content: img ? "summary_large_image" : "summary" },
    { name: "twitter:site", content: TWITTER_HANDLE },
    { name: "twitter:title", content: ogTitle },
    { name: "twitter:description", content: ogDesc },

    // Local / geo targeting — San Antonio + Austin first on every page, then
    // every major Texas city. `geoMeta()` also emits the single
    // <meta name="keywords"> tag (page keywords + sitewide local keywords),
    // so we never duplicate the keywords tag in one document.
    ...geoMeta(keywords),
  ];





  if (img) {
    meta.push(
      { property: "og:image", content: img },
      { property: "og:image:secure_url", content: img },
      { property: "og:image:width", content: DEFAULT_OG_IMAGE_WIDTH },
      { property: "og:image:height", content: DEFAULT_OG_IMAGE_HEIGHT },
      { property: "og:image:alt", content: imageAlt },
      { name: "twitter:image", content: img },
      { name: "twitter:image:alt", content: imageAlt },
    );
  }

  if (type === "article" && article) {
    if (article.publishedTime)
      meta.push({ property: "article:published_time", content: article.publishedTime });
    if (article.modifiedTime)
      meta.push({ property: "article:modified_time", content: article.modifiedTime });
    if (article.author) meta.push({ property: "article:author", content: article.author });
    if (article.section) meta.push({ property: "article:section", content: article.section });
    if (article.tags) {
      for (const tag of article.tags) {
        meta.push({ property: "article:tag", content: tag });
      }
    }
  }

  return meta;
}

/** Standard canonical link entry for a route's head().links. */
export function canonicalLink(path: string = "/") {
  return { rel: "canonical" as const, href: canonicalUrl(path) };
}

/**
 * Canonical link entry that ONLY emits when this route is the leaf match.
 * Use in parent routes that have child routes (e.g. /awnings vs
 * /awnings/in/$city) to prevent the parent's canonical from leaking into
 * child route HTML alongside the child's own canonical.
 *
 * Returns an array suitable for spreading into `links: [...]`.
 */
export function leafCanonicalLink(ctx: HeadCtx | undefined, path: string) {
  // Only emit when this route is the LEAF of the current match chain — i.e.
  // no deeper child route matched. Comparing `ctx.match.pathname` alone is
  // unreliable because TanStack populates each matched route's `pathname`
  // with its own segment, so a parent's head() would still see a match.
  // Using `ctx.matches` (the full match chain) lets us check whether this
  // route is the last one — the leaf.
  const matches = ctx?.matches ?? [];
  const selfId = ctx?.match?.routeId;
  const leafId = matches.length > 0 ? matches[matches.length - 1]?.routeId : undefined;
  // If we can't determine the chain (no matches array), fall back to a
  // pathname compare which is correct on routes without children.
  if (!leafId) {
    const current = ctx?.match?.pathname?.replace(/\/+$/, "") || "/";
    const target = path.replace(/\/+$/, "") || "/";
    return current === target ? [canonicalLink(path)] : [];
  }
  return selfId && selfId === leafId ? [canonicalLink(path)] : [];
}

/**
 * Subset of the context object TanStack Router passes to `head()`. We only
 * need `match.pathname`, but keeping this loosely typed avoids an import-cycle
 * with the router's generated types.
 */
type HeadCtx = {
  match?: { pathname?: string; routeId?: string };
  matches?: Array<{ routeId?: string; pathname?: string }>;
};

/**
 * Pull the current route's pathname out of the `head()` context and normalize
 * it (strip trailing slash so canonicalUrl(...) returns a stable URL). This
 * lets every route derive og:url / canonical from the actual matched route
 * instead of a hand-typed string that can drift away from the file's path.
 */
export function pathFromMatch(ctx: HeadCtx | undefined, fallback = "/"): string {
  const raw = ctx?.match?.pathname ?? fallback;
  if (!raw || raw === "/") return "/";
  return raw.replace(/\/+$/, "");
}

/**
 * Pathname of the deepest match (the page actually being rendered). Parent
 * routes' `head()` output concatenates into child matches, so a parent that
 * should only emit page-level schema (breadcrumbs, FAQ) for itself compares
 * this against its own path.
 */
export function leafPathFromMatches(ctx: HeadCtx | undefined, fallback = "/"): string {
  const matches = ctx?.matches ?? [];
  const raw = matches[matches.length - 1]?.pathname ?? ctx?.match?.pathname ?? fallback;
  if (!raw || raw === "/") return "/";
  return raw.replace(/\/+$/, "");
}


/**
 * Sugar over `pageMeta` that derives `path` automatically from the head()
 * context. Use this in routes so og:url / og:image / canonical always match
 * the page actually being rendered, not a stale hand-typed string.
 *
 * Example:
 *   head: (ctx) => ({ meta: routeMeta(ctx, { title, description, image }) })
 */
export function routeMeta(
  ctx: HeadCtx | undefined,
  input: Omit<PageMetaInput, "path"> & { path?: string },
): Meta[] {
  return pageMeta({ ...input, path: input.path ?? pathFromMatch(ctx) });
}

/**
 * Default share image used by any page that surfaces our Facebook feed
 * (FacebookFeed / FacebookProof embeds, "See our Day to Day work" landings).
 *
 * Centralized so every Facebook-adjacent route shares the same absolute
 * og:image / twitter:image URL — Facebook's scraper rejects relative paths,
 * and using one image here keeps social previews consistent.
 */
// Use the bundled hero image as the Facebook-feed share fallback.
// (Previously pointed at /og/facebook-feed-share.jpg which doesn't exist
// in /public, causing a 404 from social scrapers and crawl reports.)
export const FACEBOOK_SHARE_IMAGE_PATH = facebookShareFallback;

/**
 * Build the OG/Twitter meta block for any route that features our Facebook
 * feed or links out to our Facebook page. Always returns absolute image URLs
 * and an og:url derived from the route's actual pathname when `ctx` is given.
 */
export function facebookFeedMeta(
  input: {
    title: string;
    description: string;
    path?: string;
    image?: ImageInput;
    socialTitle?: string;
    socialDescription?: string;
  },
  ctx?: HeadCtx,
): Meta[] {
  return pageMeta({
    title: input.title,
    description: input.description,
    path: input.path ?? pathFromMatch(ctx),
    image: input.image ?? FACEBOOK_SHARE_IMAGE_PATH,
    socialTitle: input.socialTitle,
    socialDescription: input.socialDescription,
    imageAlt: "Texas Made Shade & Covers — see our day-to-day work on Facebook",
    type: "website",
  });
}
